/**
 * Backdrop compositor — fullscreen-quad blit of backdropFbo on top of
 * sceneFbo using premultiplied "over" blending.
 *
 * Layer 5 refactor: backdrop-filter nodes are drawn into a SEPARATE FBO
 * (backdropFbo) so they can sample sceneFbo without the self-referential
 * read/write stall. This pipeline then composites backdropFbo on top of
 * sceneFbo with default alpha blending (src=ONE, dst=ONE_MINUS_SRC_ALPHA,
 * already set globally), producing the final scene color in sceneFbo.
 *
 * The vertex shader is identical to postProcess's: a 2-triangle quad in
 * NDC, UV derived as `pos * 0.5 + 0.5`. We bind backdropFbo.tex to unit 0
 * and read it as premultiplied color.
 */

import { linkProgram } from "./glContext";

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}
`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;
uniform sampler2D uBackdrop; // premultiplied RGBA from backdropFbo
void main() {
  // Read premultiplied color straight from the texture. Default GL blend
  // (gl.ONE, gl.ONE_MINUS_SRC_ALPHA) gives us the "over" operator
  // automatically when this is drawn into sceneFbo.
  outColor = texture(uBackdrop, vUV);
}
`;

export interface BackdropCompositePipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject;
  quadBuffer: WebGLBuffer;
  uBackdrop: WebGLUniformLocation | null;
}

const QUAD_VERTS = new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]);

export function createBackdropCompositePipeline(
  gl: WebGL2RenderingContext,
): BackdropCompositePipeline | null {
  // Empty attribs record: the shader uses `layout(location = 0) in vec2 aPos`
  // directly, so we don't need any bindAttribLocation calls before linking.
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, {});
  if (!program) return null;

  const vao = gl.createVertexArray();
  const quadBuffer = gl.createBuffer();
  if (!vao || !quadBuffer) return null;

  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
  gl.bufferData(gl.ARRAY_BUFFER, QUAD_VERTS, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  return {
    program,
    vao,
    quadBuffer,
    uBackdrop: gl.getUniformLocation(program, "uBackdrop"),
  };
}

export function drawBackdropComposite(
  gl: WebGL2RenderingContext,
  pipe: BackdropCompositePipeline,
  backdropTex: WebGLTexture,
): void {
  gl.useProgram(pipe.program);
  gl.bindVertexArray(pipe.vao);
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, backdropTex);
  if (pipe.uBackdrop) gl.uniform1i(pipe.uBackdrop, 0);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}